import {
	RateCardType,
	TRateCardModifierSet,
	TRateCardModifierType,
	TSplitTrailerModifier,
} from '@types';
import {
	ContentExpander,
	Grid,
	NumberField,
	Label,
	Radio,
	Spacer
} from '@components';
import styles from './Modifiers.module.scss';
import { useRecoilValue } from 'recoil';
import {
	RateCardFormErrors, RateCardSettingsTypes, RateCardTypes, AllRateCardFuelLevyModifiers, AllRateCardSettingFuelLevyModifiers
} from '@state';
import { useClearRCSettingsError, useRateCardModifiersState } from '@hooks';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import { getRateCardIndex, getTruckSizeLabel, removeLeadingZeros } from '@utils';
import { ModifierBadge } from './ModifierBadge';

type TTruckSizeKey = keyof TSplitTrailerModifier['truck_sizes'];

const TRUCK_SIZES: TTruckSizeKey[] = ['B_DOUBLE', 'A_DOUBLE', 'B_TRIPLE'];

const EMPTY_TRUCK_SIZES: TSplitTrailerModifier['truck_sizes'] = {
	A_DOUBLE: { value: '' },
	B_DOUBLE: { value: '' },
	B_TRIPLE: { value: '' },
};

type TProps = {
	isSettings?: boolean;
	type: RateCardType
	isDisabled?: boolean;
	id?: string;
}

export const SplitTrailerModifierPanel = (props: TProps) => {
	const {
		isSettings,
		type,
		isDisabled = false,
		id
	} = props;
	const [rateCardModifiers, setRateCardModifiers] = useRateCardModifiersState();
	const rateCardSettingsTypes = useRecoilValue(RateCardSettingsTypes);
	const rateCardTypes = useRecoilValue(RateCardTypes);
	const rateCardTypeIndex = getRateCardIndex({
		rateCardSettingsTypes,
		rateCardTypes,
		id,
		isSettings: !!isSettings,
		type
	});
	const modifiers = isSettings ? rateCardModifiers.settings : rateCardModifiers.rateCard;
	const errorString = isSettings ? 'rate_card_types' : 'rate_card_prices';
	const rcFormErrors = useRecoilValue(RateCardFormErrors);
	const clearRCSettingsError = useClearRCSettingsError();

	const allRateCardModifiers = useRecoilValue(AllRateCardFuelLevyModifiers);
	const allRateCardSettingModifiers = useRecoilValue(AllRateCardSettingFuelLevyModifiers);
	const fuelLevyModifiers = isSettings ? allRateCardSettingModifiers : allRateCardModifiers;

	const setModifier = (splitTrailer: TSplitTrailerModifier | null) => {
		const update = {
			...modifiers,
			split_trailer: splitTrailer,
		} as TRateCardModifierSet;

		setRateCardModifiers({
			settings: isSettings ? update : undefined,
			rateCard: !isSettings ? update : undefined,
		});
	}

	const setChargeMethod = (newValue: string) => {
		if (newValue === 'None') {
			setModifier(null);
			return;
		}
		setModifier({
			type: TRateCardModifierType.PER_STOP,
			truck_sizes: modifiers?.split_trailer?.truck_sizes ?? EMPTY_TRUCK_SIZES,
			attracts_fuel_levy: !!fuelLevyModifiers?.split_trailer?.attracts_fuel_levy,
		});
	}

	const updateTruckSizeValue = (truckSizeKey: TTruckSizeKey, newValue: string) => {
		if (!modifiers?.split_trailer) return;
		setModifier({
			...modifiers.split_trailer,
			truck_sizes: {
				...modifiers.split_trailer.truck_sizes,
				[truckSizeKey]: { value: String(newValue) },
			},
			attracts_fuel_levy: !!fuelLevyModifiers?.split_trailer?.attracts_fuel_levy,
		});
		// @ts-ignore
		if (rcFormErrors?.[errorString]?.[rateCardTypeIndex]?.modifier_set?.split_trailer?.truck_sizes?.[truckSizeKey]?.value) {
			clearRCSettingsError(`${errorString}.${rateCardTypeIndex}.modifier_set.split_trailer.truck_sizes.${truckSizeKey}.value`)
		}
	}

	const handleOnBlur = (truckSizeKey: TTruckSizeKey, newValue: string) => {
		if (newValue.startsWith('0') && !newValue.startsWith('0.')) {
			updateTruckSizeValue(truckSizeKey, removeLeadingZeros(newValue));
		}
		if (newValue.startsWith('.')) {
			updateTruckSizeValue(truckSizeKey, `0${newValue}`)
		}
	}

	const hasError = (truckSizeKey: TTruckSizeKey) => {
		// @ts-ignore
		return rcFormErrors?.[errorString]?.[rateCardTypeIndex]?.modifier_set?.split_trailer?.truck_sizes?.[truckSizeKey]?.value?._errors?.[0] || '';
	}

	const getSectionErrors = () => {
		return TRUCK_SIZES.some((key) => hasError(key).length > 0);
	}

	const getConfiguredCount = () => {
		if (!modifiers?.split_trailer) return 0;
		return TRUCK_SIZES.filter((key) =>
			parseFloat(`${modifiers.split_trailer?.truck_sizes?.[key]?.value ?? ''}`) > 0
		).length;
	}

	if (!modifiers) return

	return (
		<ContentExpander
			title={<div className={styles.expanderTitle}>Split Trailer
				<ModifierBadge count={getConfiguredCount()} />
				{
					getSectionErrors() ?
						<span className={styles.expanderErrorContainer}>
							<ErrorOutlineOutlinedIcon />
						</span>	: null
				}
			</div>}
			showSpacer
			expanded={false}
			arrowsAsExpanderIcon
			iconColor='var(--neutral-06)'
		>
			<div className={styles.expanderContent}>
				<Label>Charge method</Label>
				<Radio.Group className={styles.radioContainer}>
					<Radio
						name={'charge_method'}
						label={'Fee per stop'}
						value={TRateCardModifierType.PER_STOP}
						onChange={(e) => setChargeMethod(e.target.value)}
						checked={modifiers?.split_trailer?.type === TRateCardModifierType.PER_STOP}
						disabled={isDisabled}
					/>
					<Radio
						name={'charge_method'}
						label={'None'}
						value={'None'}
						onChange={(e) => setChargeMethod(e.target.value)}
						checked={modifiers?.split_trailer === null}
						disabled={isDisabled}
					/>
				</Radio.Group>
				{
					modifiers?.split_trailer?.type === TRateCardModifierType.PER_STOP &&
						<Grid columns={3} columnTemplate={'repeat(3, 1fr)'} gap={'16px'} alignItems='start'>
							{
								TRUCK_SIZES.map((key) => (
									<NumberField
										key={key}
										name={key}
										value={`${modifiers.split_trailer?.truck_sizes?.[key]?.value ?? '0'}`}
										onChange={(e) => updateTruckSizeValue(key, e.target.value)}
										leftModule={<span className={styles.prefix}>$</span>}
										label={getTruckSizeLabel(key)}
										keepErrorSpace={false}
										required
										hasError={!!hasError(key)}
										errorMessage={hasError(key)}
										disabled={isDisabled}
										min={'0'}
										onBlur={(e) => handleOnBlur(key, e.target.value)}
									/>
								))
							}
						</Grid>
				}
				<Spacer size={5} />
			</div>

		</ContentExpander>
	);
}
